import { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';

import type { FormatHookParams } from '/business/shared/components/pages/question/types';

import { useSelector } from 'react-redux';
import useEmissionSourceDropdown from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeEmissionSourceDropdown';
import useAbsoluteEmissions from '/business/shared/components/pages/question/formats/staticSharedFields/useAbsoluteEmissions';
import useHowWasCollected from '/business/shared/components/pages/question/formats/staticSharedFields/useHowWasCollected';
import useMakeQuantity from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeQuantity';
import useMakeSupplierName from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeSupplierName';
import {
  QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
  QUESTION_BRANCH_GROUP_KEY_INTENSITY,
} from '/business/shared/components/pages/question/questionEnums';
import WidgetIcon from '/business/shared/images/icons/expander-icon.svg';
import getEmissionWasteTypesSelector from '/business/shared/state/selectors/form/getEmissionWasteTypesSelector';
import Button from '/shared/components/widgets/button/Button';
import WidgetAccordion from '/shared/components/widgets/widget-accordion/WidgetAccordion';
import StyledHeading from '/shared/styled/components/StyledHeading';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import {
  useGetEmissionSourceFields,
  useGetQuantityDropdownValues,
  useGetUnitDropdownValues,
} from '/business/shared/components/pages/question/useFieldUnits';
import getRefrigerantsSelector from '/business/shared/state/selectors/form/getRefrigerantsSelector';
import getDeicingChemicalTypesSelector from '/business/shared/state/selectors/form/getDeicingChemicalTypesSelector';
import { useGetEmissionsFromQuestion } from '/business/shared/hooks/useGetEmissionsFromQuestion';
import useRefrigerantType from '/business/shared/components/pages/question/formats/staticSharedFields/useRefrigerantType';
import type { EmissionTypeOption } from '/business/shared/state/reducers/emissionTypes';

export default function useFormatSharedSupplierScope1Ver2({
  question,
  questionFormatName,
  branchType,
  label,
  emissionSourcePlaceholder,
  sourceErrMsg,
}: FormatHookParams & { sourceErrMsg?: string }) {
  const [emissionSource, setEmissionSource] = useState<string | null>(null);
  const [emissionSourceForAbsolute, setEmissionSourceForAbsolute] = useState<string | null>(null);

  const _wasteTypesRaw = useSelector(getEmissionWasteTypesSelector);
  const deicingChemicals = useSelector(getDeicingChemicalTypesSelector);
  const refrigerantTypes = useSelector(getRefrigerantsSelector);
  const wasteTypes = useMemo(
    () => Array.from(new Map(_wasteTypesRaw.map((item: EmissionTypeOption) => [item.displayName, item])).values()),
    [_wasteTypesRaw],
  );
  // TODO: [WR] base on question.apiKey when available
  const { mappedForDropdown: fuels } = useGetEmissionsFromQuestion({
    question: { apiKey: 'calculation-building-emissions-other' },
  });

  const getEmissionsForDropdown = (questionFormatName: string) => {
    switch (questionFormatName) {
      case 'refrigerants':
        return refrigerantTypes;
      case 'deicing':
        return deicingChemicals;
      case 'waste-generated':
        return wasteTypes;
      case 'sold-products-fuels':
        return fuels;
      default:
        return [];
    }
  };

  const quantity = useMakeQuantity({
    quantityDropdownValues: useGetQuantityDropdownValues({
      questionFormatName,
      emissionSourcesArray: getEmissionsForDropdown(questionFormatName),
      emissionSource,
    }),
    subLabel: 'E.g. for 100 items, enter 100',
  });
  const quantityForAbsolute = useMakeQuantity({
    quantityDropdownValues: useGetQuantityDropdownValues({
      questionFormatName,
      emissionSourcesArray: getEmissionsForDropdown(questionFormatName),
      emissionSource: emissionSourceForAbsolute,
    }),
    subLabel: 'E.g. for 100 items, enter 100',
  });
  const emissionSourceDropdown = useEmissionSourceDropdown({
    valuesArray: useGetEmissionSourceFields({ questionFormatName, question, wasteTypes }),
    emissionSourcePlaceholder,
    label,
    errMsg: sourceErrMsg,
  });

  const refrigerantType = useRefrigerantType(refrigerantTypes);

  const supplierName = useMakeSupplierName({});
  const absoluteEmissions = useAbsoluteEmissions({
    intensityOrAbsoluteDropdownValue: useGetUnitDropdownValues(true),
  });
  const supplierIntensityData = useAbsoluteEmissions({
    propName: 'supplier_intensity_data',
    dropdownPropName: 'supplier_intensity_data_unit',
    label: 'Supplier intensity data:',
    intensityOrAbsoluteDropdownValue: useGetUnitDropdownValues(false),
    errMsg: 'Please enter supplier intensity data',
  });

  const howWasCollected = useHowWasCollected();

  const pageSchema = useMemo(() => {
    return [
      {
        isGroup: true,
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        render: ({ children }: { children: React.ReactNode[] }) => {
          return (
            <WidgetAccordion items={children}>
              <div className="text">
                <StyledHeading as="h2" $type="h2" $semibold>
                  Supplier intensity data
                </StyledHeading>
                <StyledParagraph $smallText>
                  Emissions data received directly from suppliers based on carbon intensity/unit (e.g., kg CO
                  <sub>2</sub>e per units of product).
                </StyledParagraph>
              </div>
              <div className="icon">
                <img src={WidgetIcon} alt="Expander icon" />
              </div>
            </WidgetAccordion>
          );
        },
      },
      {
        isGroup: true,
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        render: ({ children }: { children: React.ReactNode[] }) => {
          return (
            <WidgetAccordion items={children}>
              <div className="text">
                <StyledHeading as="h2" $type="h2" $semibold>
                  Absolute emissions
                </StyledHeading>
                <StyledParagraph $smallText>
                  Emissions data received from suppliers as a total sum (total kg or tonnes CO<sub>2</sub>e) in relation
                  to products or services your business used.
                </StyledParagraph>
              </div>
              <div className="icon">
                <img src={WidgetIcon} alt="Expander icon" />
              </div>
            </WidgetAccordion>
          );
        },
      },
      // Shared between both views section
      questionFormatName === 'refrigerants'
        ? {
            groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
            ...refrigerantType,
            onChangeCallback: (e: string) => {
              setEmissionSource(e);
            },
          }
        : {
            groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
            ...emissionSourceDropdown,
            onChangeCallback: (e: string) => {
              setEmissionSource(e);
            },
          },
      questionFormatName === 'refrigerants'
        ? {
            groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
            ...refrigerantType,
            onChangeCallback: (e: string) => {
              setEmissionSourceForAbsolute(e);
            },
          }
        : {
            groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
            ...emissionSourceDropdown,
            onChangeCallback: (e: string) => {
              setEmissionSourceForAbsolute(e);
            },
          },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...supplierName,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...supplierName,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...supplierIntensityData,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...absoluteEmissions,
      },

      // add the shared 'quantity + howWasCollected'
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...quantity,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...quantityForAbsolute,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...howWasCollected,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...howWasCollected,
      },
      // inject the buttons
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        reactElement: (
          <>
            <Button
              $primary
              testId="save-new"
              disabled={false}
              data-groupkey={QUESTION_BRANCH_GROUP_KEY_INTENSITY}
              onClick={() => {
                setQuestionGroupForValidation(QUESTION_BRANCH_GROUP_KEY_INTENSITY);
                setSaveCallback('saveNew');
              }}
            >
              {/* @i18n: Add */}
              <Trans i18nKey="business:Buttons.add" />
            </Button>
          </>
        ),
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        reactElement: (
          <>
            <Button
              $primary
              testId="save-new"
              disabled={false}
              data-groupkey={QUESTION_BRANCH_GROUP_KEY_ABSOLUTE}
              onClick={() => {
                setQuestionGroupForValidation(QUESTION_BRANCH_GROUP_KEY_ABSOLUTE);
                setSaveCallback('saveNew');
              }}
            >
              {/* @i18n: Add */}
              <Trans i18nKey="business:Buttons.add" />
            </Button>
          </>
        ),
      },
    ];
  }, [label, emissionSourcePlaceholder, emissionSource, emissionSourceForAbsolute]);

  const [saveCallback, setSaveCallback] = useState<null | 'saveNew'>(null);
  const [questionGroupForValidation, setQuestionGroupForValidation] = useState<null | string>(null);

  return {
    pageSchema,
    saveCallback,
    questionGroupForValidation,
  };
}
